// «Знания» → «Справочники»: слева список справочников (сервисы и процессы направлений) с поиском и оглавлением,
// справа — справочник. У большого сервиса справочник из частей: показывается одна часть (или обзор) за раз.
// Адрес: /knowledge/docs/<unit>#s<номер раздела> или /knowledge/docs/<unit>?p=<часть>.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ApiError, api, qs } from '../api.ts'
import { DocSectionBlock } from '../components/DocSection.tsx'
import { Icon } from '../components/Icon.tsx'
import { Empty, ErrorBox, Loading, cx } from '../components/ui.tsx'
import { docHref, formatChars, groupParts, sectionFromHash, useDocUnits, type DocPart } from '../lib/docs.ts'
import { formatDate, plural } from '../lib/format.ts'
import { useAsync, useDebouncedValue } from '../lib/hooks.ts'
import { Snippet } from '../lib/markdown.tsx'
import { Link, navigate, useLocation } from '../router.tsx'
import type { DocDetail, DocSearchHit, DocSection, DocUnit } from '../types.ts'

function shortTitle(t: string): string {
  return t.replace(/\s+—\s+справочник\s*$/i, '').replace(/\s+—\s+карта процессов.*$/i, '')
}

function IdChip({ id }: { id: string }) {
  return (
    <span className="inline-flex h-5 max-w-full items-center rounded-[5px] border border-line bg-surface-2 px-1.5 font-mono text-[11px] leading-none text-muted">
      <span className="truncate">{id}</span>
    </span>
  )
}

interface ScrollReq {
  no: number
  n: number
}

export function DocsTab({ unit }: { unit: string | null }) {
  const loc = useLocation()
  const units = useDocUnits()
  const [q, setQ] = useState('')
  const query = useDebouncedValue(q.trim(), 300)
  const hits = useAsync(
    (signal) => (query ? api.get<DocSearchHit[]>(`/api/docs${qs({ q: query })}`, { signal }) : Promise.resolve([] as DocSearchHit[])),
    [query],
    { keep: true },
  )
  const detail = useAsync(
    (signal) => (unit ? api.get<DocDetail>(`/api/docs/${encodeURIComponent(unit)}`, { signal }) : Promise.resolve(null)),
    [unit],
  )
  const [active, setActive] = useState<number | null>(null)
  const [scrollReq, setScrollReq] = useState<ScrollReq | null>(() => {
    const no = sectionFromHash(loc.hash)
    return no !== null ? { no, n: 0 } : null
  })

  const data = detail.data ?? null
  const search = new URLSearchParams(loc.search)
  const sectionParam = search.get('s')
  const partParam = search.get('p')
  const hashNo = sectionFromHash(loc.hash)
  const parts = useMemo(() => groupParts(data?.sections ?? []), [data])
  const multi = parts.some((p) => p.part)
  // текущая часть: по разделу из якоря, по ?p=, иначе первая (обзор)
  const curPart = useMemo(() => {
    if (!data) return ''
    const byHash = hashNo !== null ? data.sections.find((s) => s.section_no === hashNo)?.part : undefined
    if (byHash !== undefined) return byHash
    if (partParam !== null && data.sections.some((s) => s.part === partParam)) return partParam
    return data.sections[0]?.part ?? ''
  }, [data, hashNo, partParam])
  const shown = parts.find((p) => p.part === curPart) ?? parts[0]

  // канонический id справочника и переход к разделу из поиска (?s=<заголовок>&p=<часть>)
  useEffect(() => {
    if (!data || !unit) return
    let no = sectionFromHash(loc.hash)
    if (sectionParam) {
      const inPart = (s: DocSection) => partParam === null || s.part === partParam
      const sec = data.sections.find((s) => inPart(s) && s.section === sectionParam) ?? data.sections.find((s) => inPart(s) && s.section.includes(sectionParam))
      if (sec) no = sec.section_no
    }
    if (data.unit !== unit || sectionParam) {
      navigate(docHref(data.unit, no ?? undefined, no === null ? partParam ?? undefined : undefined), { replace: true })
    }
    if (no !== null) setScrollReq({ no, n: Date.now() })
  }, [data])

  // при смене справочника без якоря — наверх
  useEffect(() => {
    if (sectionFromHash(window.location.hash) === null) document.getElementById('main')?.scrollTo({ top: 0 })
    setActive(null)
  }, [unit])

  const goTo = (no: number) => {
    if (!data) return
    navigate(docHref(data.unit, no), { replace: true })
    setScrollReq({ no, n: Date.now() })
  }

  const goPart = (part: string) => {
    if (!data) return
    navigate(docHref(data.unit, undefined, part || undefined))
    setScrollReq(null)
    setActive(null)
    document.getElementById('main')?.scrollTo({ top: 0 })
  }

  const grouped = useMemo(() => {
    const list = units.data ?? []
    return {
      service: list.filter((u) => u.kind === 'service'),
      process: list.filter((u) => u.kind === 'process'),
    }
  }, [units.data])

  const listPane = (
    <aside
      aria-label="Справочники"
      className={cx(
        'scroll-thin flex flex-col gap-3 min-[1100px]:sticky min-[1100px]:top-4 min-[1100px]:max-h-[calc(100dvh-56px-32px)] min-[1100px]:overflow-y-auto min-[1100px]:pr-1',
        unit ? 'hidden min-[1100px]:flex' : 'flex',
      )}
    >
      <div className="relative">
        <Icon name="search" size={15} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
        <input
          type="search"
          className="ctl h-9 pl-8"
          placeholder="Поиск по справочникам…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Поиск по справочникам"
        />
      </div>

      {query ? (
        <SearchResults hits={hits.data ?? []} terms={query.split(/\s+/)} loading={hits.loading} error={hits.error} onPick={() => setQ('')} />
      ) : units.error && !units.data ? (
        <ErrorBox error={units.error} onRetry={units.reload} />
      ) : !units.data ? (
        <Loading rows={5} />
      ) : units.data.length === 0 ? (
        <Empty icon="book">Справочников пока нет — они появятся после синхронизации базы знаний.</Empty>
      ) : (
        <>
          {grouped.service.length ? (
            <UnitGroup
              title="Сервисы"
              units={grouped.service}
              current={data?.unit ?? unit}
              detail={data}
              parts={parts}
              curPart={curPart}
              active={active}
              onSection={goTo}
              onPart={goPart}
            />
          ) : null}
          {grouped.process.length ? (
            <UnitGroup
              title="Процессы направлений"
              units={grouped.process}
              current={data?.unit ?? unit}
              detail={data}
              parts={parts}
              curPart={curPart}
              active={active}
              onSection={goTo}
              onPart={goPart}
            />
          ) : null}
        </>
      )}
    </aside>
  )

  return (
    <div className="grid grid-cols-1 items-start gap-6 min-[1100px]:grid-cols-[300px_minmax(0,1fr)]">
      {listPane}
      <div className="min-w-0">
        {!unit ? (
          <div className="hidden min-[1100px]:block">
            <Empty icon="book">
              <div className="font-medium text-fg">Выбери справочник слева</div>
              <div className="mt-0.5 text-[12.5px]">
                В справочнике сервиса — экраны и кнопки, API, фоновые задачи, данные, контракты, бизнес-правила, эксплуатация и грабли.
              </div>
            </Empty>
          </div>
        ) : detail.error && !data ? (
          detail.error instanceof ApiError && detail.error.status === 404 ? (
            <Empty icon="book">Справочника «{unit}» нет</Empty>
          ) : (
            <ErrorBox error={detail.error} onRetry={detail.reload} />
          )
        ) : !data ? (
          <div className="panel px-7 py-6">
            <Loading rows={6} />
          </div>
        ) : (
          <DocView
            key={`${data.unit}:${shown?.part ?? ''}`}
            detail={data}
            parts={multi ? parts : []}
            shown={shown}
            scrollReq={scrollReq}
            onActive={setActive}
            onSection={goTo}
            onPart={goPart}
            units={units.data ?? []}
          />
        )}
      </div>
    </div>
  )
}

function SearchResults({
  hits,
  terms,
  loading,
  error,
  onPick,
}: {
  hits: DocSearchHit[]
  terms: string[]
  loading: boolean
  error: Error | null
  onPick: () => void
}) {
  if (error) return <ErrorBox error={error} />
  if (!hits.length) return <div className="px-1 py-4 text-center text-[13px] text-muted">{loading ? 'Ищу…' : 'Ничего не нашлось'}</div>
  return (
    <ul className="flex flex-col gap-1.5" aria-label="Найдено в справочниках">
      {hits.map((h, i) => (
        <li key={`${h.unit}-${h.part}-${h.section}-${i}`}>
          <Link
            to={`/knowledge/docs/${encodeURIComponent(h.unit)}?s=${encodeURIComponent(h.section)}${h.part ? `&p=${encodeURIComponent(h.part)}` : ''}`}
            onClick={onPick}
            className="block rounded-card border border-line bg-surface px-3 py-2.5 shadow-card hover:border-line-strong"
          >
            <div className="truncate text-[13px] font-semibold text-fg">{h.section}</div>
            <div className="truncate text-[11.5px] text-subtle">
              {shortTitle(h.title)}
              {h.part ? ` · ${h.part_title || h.part}` : ''}
            </div>
            <Snippet text={h.snippet} terms={terms} className="mt-1.5 line-clamp-3 text-[13px] leading-snug break-words text-fg-2" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

function SectionLinks({ sections, active, onSection }: { sections: DocSection[]; active: number | null; onSection: (no: number) => void }) {
  return (
    <>
      {sections.map((s) => (
        <button
          key={s.section_no}
          type="button"
          onClick={() => onSection(s.section_no)}
          aria-current={active === s.section_no ? 'location' : undefined}
          className={cx(
            'relative block w-full rounded-ctl px-2 py-1 text-left text-[12.5px] leading-snug break-words transition-colors',
            active === s.section_no ? 'font-semibold text-accent' : 'text-muted hover:bg-surface-2 hover:text-fg',
          )}
        >
          {active === s.section_no ? <span aria-hidden="true" className="absolute top-1.5 bottom-1.5 -left-[9px] w-[2px] rounded bg-accent" /> : null}
          {s.section}
        </button>
      ))}
    </>
  )
}

function UnitGroup({
  title,
  units,
  current,
  detail,
  parts,
  curPart,
  active,
  onSection,
  onPart,
}: {
  title: string
  units: DocUnit[]
  current: string | null
  detail: DocDetail | null
  parts: DocPart[]
  curPart: string
  active: number | null
  onSection: (no: number) => void
  onPart: (part: string) => void
}) {
  const multi = parts.some((p) => p.part)
  return (
    <section>
      <h2 className="lbl px-1 pb-1.5">{title}</h2>
      <ul className="flex flex-col gap-1">
        {units.map((u) => {
          const on = u.unit === current
          return (
            <li key={u.unit}>
              <Link
                to={docHref(u.unit)}
                aria-current={on ? 'page' : undefined}
                className={cx(
                  'block rounded-card border px-3 py-2.5 transition-colors',
                  on ? 'border-line bg-surface shadow-card' : 'border-transparent hover:bg-surface-2',
                )}
              >
                <div className={cx('line-clamp-2 text-[13.5px] leading-snug font-semibold', on ? 'text-fg' : 'text-fg-2')}>{shortTitle(u.title)}</div>
                {u.service_ids.length ? (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {u.service_ids.slice(0, 4).map((s) => (
                      <IdChip key={s} id={s} />
                    ))}
                  </div>
                ) : null}
                <div className="tnum mt-1.5 text-[11.5px] text-subtle">
                  {u.parts ? `${u.parts} ${plural(u.parts, ['часть', 'части', 'частей'])} · ` : ''}
                  {u.sections} разд. · {formatChars(u.chars)} · {formatDate(u.updated_at)}
                </div>
              </Link>
              {on && detail && detail.unit === u.unit ? (
                <nav aria-label="Оглавление" className="mt-1 mb-2 ml-3 border-l border-line pl-2">
                  {multi ? (
                    parts.map((p) => (
                      <div key={p.part || '_'}>
                        <button
                          type="button"
                          onClick={() => onPart(p.part)}
                          aria-current={p.part === curPart ? 'true' : undefined}
                          className={cx(
                            'block w-full rounded-ctl px-2 py-1 text-left text-[12.5px] leading-snug break-words transition-colors',
                            p.part === curPart ? 'font-semibold text-fg' : 'text-fg-2 hover:bg-surface-2',
                          )}
                        >
                          {p.title}
                        </button>
                        {p.part === curPart ? (
                          <div className="mb-1 ml-2 border-l border-line pl-2">
                            <SectionLinks sections={p.sections} active={active} onSection={onSection} />
                          </div>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <SectionLinks sections={detail.sections} active={active} onSection={onSection} />
                  )}
                </nav>
              ) : null}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

function DocView({
  detail,
  parts,
  shown,
  scrollReq,
  onActive,
  onSection,
  onPart,
  units,
}: {
  detail: DocDetail
  /** Части справочника большого сервиса; пусто — единый файл. */
  parts: DocPart[]
  shown: DocPart | undefined
  scrollReq: ScrollReq | null
  onActive: (no: number | null) => void
  onSection: (no: number) => void
  onPart: (part: string) => void
  units: DocUnit[]
}) {
  const sections = shown?.sections ?? detail.sections
  const first = detail.sections[0]
  const meta = units.find((u) => u.unit === detail.unit)
  const [eagerUpTo, setEagerUpTo] = useState(() => scrollReq?.no ?? 0)
  const [sourceOpen, setSourceOpen] = useState(false)
  const lastScrolled = useRef<number | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollReq) setEagerUpTo((e) => Math.max(e, scrollReq.no))
  }, [scrollReq])

  // переход к разделу — после того как он и всё над ним отрисованы. Пока догружается шрифт и
  // дорисовываются соседние разделы, цель «приколота» к верху (до первого действия пользователя).
  useLayoutEffect(() => {
    if (!scrollReq || eagerUpTo < scrollReq.no || lastScrolled.current === scrollReq.n) return
    lastScrolled.current = scrollReq.n
    const target = () => document.getElementById(`s${scrollReq.no}`)
    target()?.scrollIntoView({ block: 'start' })
    const root = document.getElementById('main')
    const body = bodyRef.current
    if (!root || !body) return
    let pinned = true
    const release = () => {
      pinned = false
    }
    const repin = () => {
      if (pinned) target()?.scrollIntoView({ block: 'start' })
    }
    root.addEventListener('wheel', release, { passive: true })
    root.addEventListener('touchstart', release, { passive: true })
    root.addEventListener('mousedown', release)
    window.addEventListener('keydown', release)
    const ro = new ResizeObserver(repin)
    ro.observe(body)
    void document.fonts?.ready.then(repin)
    const t = window.setTimeout(release, 2500)
    return () => {
      release()
      ro.disconnect()
      window.clearTimeout(t)
      root.removeEventListener('wheel', release)
      root.removeEventListener('touchstart', release)
      root.removeEventListener('mousedown', release)
      window.removeEventListener('keydown', release)
    }
  }, [scrollReq, eagerUpTo])

  // подсветка текущего раздела в оглавлении
  useEffect(() => {
    const root = document.getElementById('main')
    if (!root) return
    let raf = 0
    const update = () => {
      raf = 0
      const top = root.getBoundingClientRect().top + 96
      let cur: number | null = null
      for (const el of root.querySelectorAll<HTMLElement>('section[data-section]')) {
        if (el.getBoundingClientRect().top <= top) cur = Number(el.dataset.section)
        else break
      }
      onActive(cur ?? sections[0]?.section_no ?? null)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    update()
    root.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      root.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [sections, onActive])

  if (!first) return <Empty icon="book">В справочнике нет разделов</Empty>
  const title = first.title
  const source = first.source
  const chars = detail.sections.reduce((n, s) => n + s.body.length, 0)
  const partCount = parts.filter((p) => p.part).length

  return (
    <article className="min-w-0">
      <div className="mb-3 min-[1100px]:hidden">
        <Link to="/knowledge/docs" className="inline-flex items-center gap-1 text-[13px] text-accent hover:underline">
          <Icon name="chevronLeft" size={14} />
          Все справочники
        </Link>
      </div>
      <header className="panel mb-4 px-4 py-4 min-[720px]:px-7 min-[720px]:py-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="stbadge">{first.kind === 'process' ? 'процессы направления' : 'справочник сервиса'}</span>
          {first.service_ids.map((s) => (
            <IdChip key={s} id={s} />
          ))}
          <span className="tnum ml-auto text-[12px] text-subtle">
            {partCount ? `${partCount} ${plural(partCount, ['часть', 'части', 'частей'])} · ` : ''}
            {detail.sections.length} разд. · {formatChars(meta?.chars ?? chars)} · обновлён {formatDate(meta?.updated_at ?? first.updated_at)}
          </span>
        </div>
        <h2 className="mt-2.5 text-[22px] leading-tight font-[700] tracking-[-0.02em] break-words text-fg max-[720px]:text-[19px]">{title}</h2>
        {source ? (
          <div className="mt-2 font-mono text-[11.5px] leading-relaxed break-words text-muted">
            {sourceOpen || source.length <= 260 ? source : `${source.slice(0, 240).trimEnd()}…`}
            {source.length > 260 ? (
              <button type="button" className="ml-1.5 font-sans text-[12px] text-accent hover:underline" onClick={() => setSourceOpen((v) => !v)}>
                {sourceOpen ? 'свернуть' : 'полностью'}
              </button>
            ) : null}
          </div>
        ) : null}
        {detail.other_units.length ? (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12.5px] text-muted">
            Ещё справочники этого сервиса:
            {detail.other_units.map((u) => (
              <Link key={u} to={docHref(u)} className="min-w-0 text-accent [overflow-wrap:anywhere] hover:underline">
                {u}
              </Link>
            ))}
          </div>
        ) : null}
        {partCount ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="lbl" htmlFor="doc-part">
              Часть
            </label>
            <select id="doc-part" className="ctl max-w-full min-[720px]:max-w-[560px]" value={shown?.part ?? ''} onChange={(e) => onPart(e.target.value)}>
              {parts.map((p) => (
                <option key={p.part || '_'} value={p.part}>
                  {p.title}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {/* компактное оглавление для узких экранов */}
        <div className="mt-3 min-[1100px]:hidden">
          <label className="sr-only" htmlFor="doc-toc">
            Раздел
          </label>
          <select id="doc-toc" className="ctl" value="" onChange={(e) => e.target.value !== '' && onSection(Number(e.target.value))}>
            <option value="">Перейти к разделу…</option>
            {sections.map((s) => (
              <option key={s.section_no} value={s.section_no}>
                {s.section}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div ref={bodyRef} className="panel px-5 py-6 min-[720px]:px-8 min-[720px]:py-7">
        {shown?.part ? <h3 className="mb-5 border-b border-line pb-3 text-[15px] leading-snug font-semibold text-fg-2">{shown.title}</h3> : null}
        {sections.map((s) => (
          <DocSectionBlock key={s.id} sec={s} eager={s.section_no <= eagerUpTo} onAnchor={onSection} />
        ))}
      </div>
    </article>
  )
}
